import React from 'react';
import PropTypes from 'prop-types';
import CommonAppSetup from '../../../common/common-app-setup/CommonAppSetup';
import AjaxSpinner from '../../ajax-spinner/AjaxSpinner';
import InputContainer from '../input-container/InputContainer';
import Components from '../../../common/Components';
import joinClassNames from "../../../common/JoinClassNames";
import childrenPosition from '../ChildPosition';

const requiredLabelOrChild = (props, propName, componentName) => {
	if (!props.children && !props.label) {
		return new Error(`One of 'children' or 'label' is required by '${componentName}' component.`);
	}
	if (props.children && !React.isValidElement(props.children)) {
		return new Error(`Prop 'children' must be a valid React element for '${componentName}' component.`);
	}
	if (props.label && !( (typeof props.label === 'string') || (React.isValidElement(props.label)) )) {
		return new Error(`Prop 'label' must be a string for '${componentName}' component.`);
	}
};

const propTypes = {
	// should the button be wrapped in an InputContainer?
	addWrapper: PropTypes.bool,

	// the button is busy and cannot be clicked. It will show a ajax spinner.
	busy: PropTypes.bool,

	// children node that allows you to pass in other elements to the button
	children: requiredLabelOrChild,

	// put the children before or after the label
	childrenPosition: PropTypes.string,

	// additional classNames to put on the component
	className: PropTypes.string,

	// if true then the input will be disabled
	disabled: PropTypes.bool,

	// html id of the button
	id: PropTypes.string,

	// a react ref that is linked to the button, don't use directly, just pass ref={} to forwardRef
	innerRef: PropTypes.object,

	// the label/placeholder to show
	label: requiredLabelOrChild,

	// button was clicked (won't fire if disabled)
	onClick: PropTypes.func.isRequired,

	// When the component mounts should the input be given focus.
	setInitialFocus: PropTypes.bool,

	// The size of the spinner in px
	spinnerSize: PropTypes.number,
};

const defaultProps = {
	addWrapper: false,
	busy: false,
	children: null,
	childrenPosition: childrenPosition.AFTER,
	className: undefined,
	disabled: false,
	id: undefined,
	label: undefined,
	setInitialFocus: false,
	spinnerSize: 20,
};

class Button extends React.Component {
	constructor(props) {
		super(props);

		//create a ref to store the button DOM element
		this.buttonRef = this.props.innerRef ? this.props.innerRef : React.createRef();
	}

	componentDidMount() {
		if (this.props.setInitialFocus) {
			this.buttonRef.current.focus();
		}
	}

	positionChildren(position) {
		return (
			position === this.props.childrenPosition && this.props.children ? this.props.children : null
		);
	}

	render() {
		let result = (
			<button
				className={joinClassNames(
					this.props.className,
					this.props.children ? null : 'button--noChildren',
					this.props.label ? null : 'button--noLabel',
					CommonAppSetup.getClassName(Components.BUTTON, 'button'),
					(this.props.busy ? 'busy' : null)
				)}
				disabled={this.props.disabled || this.props.busy ? 'disabled' : null}
				id={this.props.id}
				onClick={this.props.onClick}
				ref={this.buttonRef}
				type="button"
				aria-live="polite"
				aria-relevant="text"
			>
				{this.positionChildren(childrenPosition.BEFORE)}
				{this.props.busy && <AjaxSpinner size={this.props.spinnerSize} className={CommonAppSetup.getClassName(Components.BUTTON, 'busy')}/>}
				{this.props.label && <span className="button-label">{this.props.label}</span>}
				{this.positionChildren(childrenPosition.AFTER)}
			</button>
		);

		return this.props.addWrapper ? <InputContainer className={CommonAppSetup.getClassName(Components.BUTTON, 'wrapper')}>{result}</InputContainer> : result;
	}
}

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

const forwardRefButton = React.forwardRef((props, ref) => <Button innerRef={ref} {...props}/>);
// children position attached to the button/forwardRef is deprecated and will be removed in a future version
forwardRefButton.CHILDREN_POSITION = childrenPosition;

export default forwardRefButton;